import {
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  PopoverTrigger,
  Portal,
  Text,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import ContentCopySharpIcon from '@gamma/ui/icons/ContentCopySharpIcon';
import DashboardSharpIcon from '@gamma/ui/icons/DashboardSharpIcon';
import DatabaseSharpIcon from '@gamma/ui/icons/DatabaseSharpIcon';
import KeyboardArrowDownSharpIcon from '@gamma/ui/icons/KeyboardArrowDownSharpIcon';
import ListAltSharpIcon from '@gamma/ui/icons/ListAltSharpIcon';
import NorthEastSharpIcon from '@gamma/ui/icons/NorthEastSharpIcon';
import { RouterLink } from '@gamma/ui/navigation';
import { Dispatch, SetStateAction, useContext, useEffect, useRef } from 'react';
import { ROUTES } from '../../../../shared/constants';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers';
import { useEntitiesContext } from '../../context';
import { SuppressEntityMenuItem } from '../../../../shared/components/SuppressEntityMenuItem';
import { logScaleEntityPivot } from '@investigator/shared/utils';
import { isPublicIPAddress } from '../../utils';
import { ExternalIPLinksMenu } from '../ExternalIPLinksMenu';

const { copied, copyValue, controls, viewDetails, viewDetections } =
  i18n.pages.detections;

interface EntityPopoverButtonProps {
  entityName: string;
  isStandalone?: boolean;
  contextMenuOpen?: boolean;
  isHoveredEntity?: boolean;
  handleEntityClick: () => void;
  setContextMenuOpen: Dispatch<SetStateAction<boolean>>;
  handleSuppressEntity: () => void;
  isSuppressed?: boolean;
  isDisabled?: boolean;
  isLoadingSuppression?: boolean;
  start: number;
  end: number;
}

export const EntityPopoverButton = ({
  entityName,
  isStandalone,
  contextMenuOpen,
  isHoveredEntity,
  handleEntityClick,
  setContextMenuOpen,
  handleSuppressEntity,
  isSuppressed,
  isDisabled,
  isLoadingSuppression,
  start,
  end,
}: EntityPopoverButtonProps) => {
  const { userRole } = useContext(AuthContext);

  const {
    analysisModal: { onOpen: onEntityModalOpen },
  } = useEntitiesContext();

  const toast = useToast();
  const { hasCopied, onCopy } = useClipboard(entityName);

  const detectionsPivotParams = `start=${start}&end=${end}&source_name=${entityName}`;

  useEffect(() => {
    if (hasCopied) {
      toast({
        position: 'bottom-right',
        status: 'success',
        description: copied,
      });
    }
  }, [hasCopied, toast]);

  const logScaleEntityQuery =
    start && end && entityName
      ? logScaleEntityPivot({
          start,
          end,
          entityName: entityName,
        })
      : '';
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <PopoverTrigger>
      <ButtonGroup isAttached>
        <Button
          size="sm"
          variant="ghost"
          ref={buttonRef}
          colorScheme="gray"
          color={isStandalone || isHoveredEntity ? 'white' : 'text.primary'}
          border={'1px solid'}
          justifyContent="start"
          borderTopRightRadius={isStandalone ? '4px !important' : 'base'}
          borderBottomRightRadius={isStandalone ? '4px !important' : 'base'}
          borderColor={
            isStandalone || isHoveredEntity ? 'whiteAlpha.300' : 'transparent'
          }
          backgroundColor={
            isStandalone && isHoveredEntity
              ? '#393939 !important'
              : isStandalone || isHoveredEntity
                ? 'gray.600'
                : ''
          }
          width={isStandalone ? 'auto' : isHoveredEntity ? '110px' : 'auto'}
          maxW={isStandalone ? 'auto' : '150px'}
          onClick={(e) => {
            e.preventDefault();
            handleEntityClick();
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            setContextMenuOpen?.(true);
          }}
        >
          <Text
            as="span"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            pointerEvents="none"
          >
            {entityName}
          </Text>
        </Button>
        {isHoveredEntity && (
          <Menu
            isOpen={contextMenuOpen}
            offset={
              !isStandalone
                ? [-110, 0]
                : [
                    buttonRef.current?.clientWidth
                      ? -(buttonRef.current?.clientWidth + 2)
                      : 0,
                    0,
                  ]
            }
          >
            {!isStandalone ? (
              <MenuButton
                as={IconButton}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                color="white"
                aria-label=""
                border={'1px solid'}
                borderColor="whiteAlpha.300"
                borderLeft={0}
                backgroundColor="gray.600"
                icon={<KeyboardArrowDownSharpIcon />}
                onClick={(e) => {
                  e.preventDefault();
                  setContextMenuOpen?.(true);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenuOpen?.(true);
                }}
              />
            ) : (
              <MenuButton
                border={0}
                onClick={(e) => {
                  e.preventDefault();
                  setContextMenuOpen?.(true);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenuOpen?.(true);
                }}
              />
            )}
            <Portal>
              <MenuList>
                <MenuGroup>
                  <MenuItem
                    as={Link}
                    _hover={{
                      textDecoration: 'none',
                    }}
                    onClick={() => onEntityModalOpen()}
                  >
                    <HStack>
                      <DashboardSharpIcon />
                      <Text>{viewDetails}</Text>
                    </HStack>
                  </MenuItem>
                  <MenuItem
                    target="_blank"
                    as={RouterLink}
                    icon={<DatabaseSharpIcon />}
                    to={{
                      pathname: ROUTES.logSearch,
                      search: logScaleEntityQuery,
                    }}
                  >
                    <HStack>
                      <Text>{controls.viewIpLogs}</Text>
                      <NorthEastSharpIcon />
                    </HStack>
                  </MenuItem>
                  <MenuItem
                    as={RouterLink}
                    icon={<ListAltSharpIcon />}
                    to={{
                      pathname: ROUTES.detections,
                      search: detectionsPivotParams,
                    }}
                  >
                    {viewDetections}
                  </MenuItem>
                </MenuGroup>
                <MenuDivider />
                <MenuGroup>
                  {userRole !== 'viewer' && handleSuppressEntity && (
                    <SuppressEntityMenuItem
                      isSuppressed={isSuppressed}
                      isDisabled={isDisabled}
                      isLoading={isLoadingSuppression}
                      onClick={handleSuppressEntity}
                      suppressText={controls.suppressEntity}
                      unsuppressText={controls.unSuppressEntity}
                    />
                  )}
                  <MenuItem onClick={onCopy} icon={<ContentCopySharpIcon />}>
                    {copyValue}
                  </MenuItem>
                </MenuGroup>
                {entityName && isPublicIPAddress(entityName) && (
                  <ExternalIPLinksMenu
                    entityName={entityName}
                    textPrefix={controls.searchIn}
                    useMenuGroup
                  />
                )}
              </MenuList>
            </Portal>
          </Menu>
        )}
      </ButtonGroup>
    </PopoverTrigger>
  );
};
